import type { PageOcrState } from 'features/files/hooks/usePageOcr'
import * as React from 'react'
import { Page, PageProps } from 'react-pdf'
import styled from 'styled-components'

import { OcrTextOverlay } from './OcrTextOverlay'

export interface PdfPageWithOcrProps extends Omit<PageProps, 'renderMode'> {
  pageNumber: number
  width: number
  url: string
  ocrEnabled: boolean
  ocrDebug: boolean
  runOcr: (url: string, pageNumber: number) => Promise<void>
  pageState: PageOcrState | undefined
}

const RawPdfPageWithOcr: React.FC<PdfPageWithOcrProps> = ({
  pageNumber,
  width,
  url,
  ocrEnabled,
  ocrDebug,
  runOcr,
  pageState,
  ...pageProps
}) => {
  // Fire OCR as soon as the user enables it (or when this page mounts with
  // OCR already on). Guard against re-triggering on error.
  React.useEffect(() => {
    if (!ocrEnabled) return
    const status = pageState?.status
    if (status === 'done' || status === 'loading' || status === 'error') return
    runOcr(url, pageNumber)
  }, [ocrEnabled, pageState, runOcr, url, pageNumber])

  const showOverlay =
    ocrEnabled &&
    pageState?.status === 'done' &&
    pageState.words.length > 0 &&
    pageState.ocrSourceWidth > 0

  return (
    <PageWrapper>
      <Page
        {...pageProps}
        pageNumber={pageNumber}
        renderMode='canvas'
        width={width}
        scale={1.0}
      />
      {showOverlay && (
        <OcrTextOverlay
          words={pageState!.words}
          displayWidth={width}
          ocrSourceWidth={pageState!.ocrSourceWidth}
          debug={ocrDebug}
        />
      )}
    </PageWrapper>
  )
}

export const PdfPageWithOcr = React.memo(RawPdfPageWithOcr)

const PageWrapper = styled.div`
  position: relative;
  /* Shrink-wrap the canvas so the overlay knows the exact page bounds */
  display: inline-block;
  line-height: 0;
`
